// src/components/ui/tables/CriticalPointsTable.jsx
import React, { useState, useMemo, useCallback } from "react";
import { Modal, Tooltip, Pagination } from "@mui/material";
import { useVehicleSettingsOperations } from "../../../hooks/useVehicleSettingsOperations";
import { useNotification } from "../../../context/NotificationContext";
import CopyIcon from "../../../assets/svg/components/copy.svg";
import LocationIcon from "../../../assets/svg/maps/Icon_tractor.svg";
import PropTypes from "prop-types";
import LoadingAnimation from "../../loading/LoadingAnimation";
import { useLayout } from "../../../context/LayoutContext";

// Función para truncar texto
function truncateText(text = "", maxLength = 30) {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
}

export function CriticalPointsTableAdmi({ fixedColumnsMode = 2 }) {
  // Estados para la paginación y búsqueda
  const [page, setPage] = useState(1);
  const [limit] = useState(5);
  const [search, setSearch] = useState("");
  
  // Obtenemos los hooks desde useVehicleSettingsOperations
  const { 
    useCriticalPoints,
    useCreateCriticalPoint,
    useUpdateCriticalPoint,
    useDeleteCriticalPoint,
    useToggleCriticalPointStatus
  } = useVehicleSettingsOperations();
  
  // Utilizamos los hooks con la nueva estructura
  const { data, isLoading, isError } = useCriticalPoints();
  
  // Operaciones CRUD con los nuevos hooks
  const createPointMutation = useCreateCriticalPoint();
  const updatePointMutation = useUpdateCriticalPoint();
  const deleteMutation = useDeleteCriticalPoint();
  const toggleStatusMutation = useToggleCriticalPointStatus();
  
  // Procesamos los datos para filtrado y paginación en el frontend
  const allPoints = data?.data || [];
  
  // Filtrar los puntos críticos según el término de búsqueda
  const filteredPoints = useMemo(() => {
    if (!search) return allPoints;
    
    const searchTermLower = search.toLowerCase();
    return allPoints.filter(
      point => 
        (point.point_name && point.point_name.toLowerCase().includes(searchTermLower))
    );
  }, [allPoints, search]);
  
  // Calcular total de páginas
  const totalPages = Math.ceil(filteredPoints.length / limit);
  
  // Obtener elementos para la página actual
  const paginatedPoints = useMemo(() => {
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    return filteredPoints.slice(startIndex, endIndex);
  }, [filteredPoints, page, limit]);

  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);

  const { showNotification } = useNotification();
  const { theme } = useLayout();

  if (isLoading) return <LoadingAnimation />;
  if (isError) return <div className={`GM__${theme}-error-message`}>Hubo un error al cargar los puntos críticos.</div>;

  // Manejadores de eventos
  const handleOpenModal = (point = null) => {
    setEditData(point);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setEditData(null);
    setShowModal(false);
  };

  const handleDelete = (id) => {
    if (window.confirm("¿Eliminar este punto crítico?")) {
      deleteMutation.mutate(id);
    }
  };

  const handleSave = async (formValues) => {
    try {
      const pointData = {
        point_name: formValues.point_name,
        start_latitude: formValues.start_latitude,
        start_longitude: formValues.start_longitude,
        end_latitude: formValues.end_latitude,
        end_longitude: formValues.end_longitude,
        is_active: formValues.is_active,
        google_maps_link: formValues.google_maps_link
      };

      if (formValues.id) {
        // Actualizar punto existente
        await updatePointMutation.mutateAsync({
          id: formValues.id,
          data: pointData
        });
      } else {
        // Crear nuevo punto
        await createPointMutation.mutateAsync(pointData);
      }
      handleCloseModal();
    } catch (error) {
      showNotification({
        message: `Error al ${formValues.id ? 'actualizar' : 'crear'} punto crítico: ${error.message || 'Error desconocido'}`,
        severity: "error",
      });
    }
  };

  const handleToggleStatus = (point) => {
    toggleStatusMutation.mutate({
      id: point.id,
      isActive: !point.is_active
    });
  };

  const handleCopy = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        showNotification({
          message: "¡Copiado exitosamente!",
          severity: "success",
        });
      })
      .catch(() => {
        showNotification({
          message: "Error al copiar.",
          severity: "error",
        });
      });
  };

  const handleViewOnMap = (point) => {
    if (point.google_maps_link) {
      window.open(point.google_maps_link, '_blank');
    } else {
      // Crear URL de Google Maps basada en coordenadas
      const mapUrl = `https://www.google.com/maps/dir/?api=1&origin=${point.start_latitude},${point.start_longitude}&destination=${point.end_latitude},${point.end_longitude}`;
      window.open(mapUrl, '_blank');
    }
  };
  
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  return (
    <div className={`GM__${theme}-vehicle-table-wrapper`}>
      <div className={`GM__${theme}-header-section`}>
        <h2 className={`GM__${theme}-section-title`}>Puntos Críticos</h2>
        <div className={`GM__${theme}-filter-container`}>
          <input
            type="text"
            placeholder="Filtrar por nombre..."
            value={search}
            onChange={handleSearchChange}
            className={`GM__${theme}-search-input`}
          />
          <button
            className={`GM__${theme}-add-button`}
            onClick={() => handleOpenModal(null)}
          >
            Agregar nuevo punto crítico
          </button>
        </div>
      </div>
      <div className={`GM__${theme}-vehicle-table-wrapper-table`}>
        <table
          className={`GM__${theme}-vehicle-table fixed-columns-${fixedColumnsMode}`}
        >
          <thead>
            <tr>
              <th
                className={`${
                  fixedColumnsMode === 1 || fixedColumnsMode === 3
                    ? "fixed-column fixed-column-1"
                    : ""
                }`}
              >
                ID
              </th>
              <th
                className={`${
                  fixedColumnsMode === 2 || fixedColumnsMode === 3
                    ? "fixed-column fixed-column-2"
                    : ""
                }`}
              >
                Nombre del Punto
              </th>
              <th>Coord. Inicio</th>
              <th>Coord. Fin</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {paginatedPoints.length > 0 ? (
              paginatedPoints.map((point) => (
                <tr key={point.id}>
                  <td
                    className={`GM__${theme}-copyable-cell ${
                      fixedColumnsMode === 1 || fixedColumnsMode === 3
                        ? "fixed-column fixed-column-1"
                        : ""
                    }`}
                  >
                    <Tooltip title={point.id?.toString() || "—"}>
                      <span>{truncateText(point.id?.toString() || "", 10)}</span>
                    </Tooltip>
                    <button
                      className={`GM__${theme}-copy-icon`}
                      onClick={() => handleCopy(point.id?.toString() || "")}
                      aria-label="Copiar ID"
                    >
                      <img src={CopyIcon} alt="Copiar" />
                    </button>
                  </td>

                  <td
                    className={`GM__${theme}-copyable-cell ${
                      fixedColumnsMode === 2 || fixedColumnsMode === 3
                        ? "fixed-column fixed-column-2"
                        : ""
                    }`}
                  >
                    <Tooltip title={point.point_name || "—"}>
                      <span>{truncateText(point.point_name || "", 15)}</span>
                    </Tooltip>
                    {point.point_name && (
                      <button
                        className={`GM__${theme}-copy-icon`}
                        onClick={() => handleCopy(point.point_name || "")}
                        aria-label="Copiar nombre"
                      >
                        <img src={CopyIcon} alt="Copiar" />
                      </button>
                    )}
                  </td>

                  <td>
                    {point.start_latitude}, {point.start_longitude}
                  </td>
                  
                  <td>
                    {point.end_latitude}, {point.end_longitude}
                  </td>

                  <td>{point.is_active ? "Activo" : "Inactivo"}</td>

                  <td className={`GM__${theme}-action-buttons`}>
                    <button
                      className={`GM__${theme}-map-button`}
                      onClick={() => handleViewOnMap(point)}
                      title="Ver en mapa"
                    >
                      <img src={LocationIcon} alt="Ver en mapa" />
                    </button>
                    <button
                      className={`GM__${theme}-edit-button`}
                      onClick={() => handleOpenModal(point)}
                    >
                      Editar
                    </button>
                    <button
                      className={`GM__${theme}-toggle-button`}
                      onClick={() => handleToggleStatus(point)}
                    >
                      {point.is_active ? "Desactivar" : "Activar"}
                    </button>
                    <button
                      className={`GM__${theme}-delete-button`}
                      onClick={() => handleDelete(point.id)}
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className={`GM__${theme}-empty-message`}>
                  {search ? "No se encontraron puntos críticos con ese filtro" : "No hay puntos críticos registrados"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {/* Paginación */}
      {totalPages > 1 && (
        <div className={`GM__${theme}-pagination-container`}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handleChangePage}
            shape="rounded"
            className={`GM__${theme}-custom-pagination`}
          />
        </div>
      )}

      {/* Modal de formulario */}
      <Modal
        open={showModal}
        onClose={handleCloseModal}
        className={`GM__modal-container-${theme}`}
        BackdropProps={{
          style: { backgroundColor: "rgba(0, 0, 0, 0.5)" },
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CriticalPointForm
          data={editData}
          onSave={handleSave}
          onCancel={handleCloseModal}
          theme={theme}
        />
      </Modal>
    </div>
  );
}

CriticalPointsTableAdmi.propTypes = {
  fixedColumnsMode: PropTypes.oneOf([1, 2, 3]),
};

function CriticalPointForm({ data, onSave, onCancel, theme }) {
  const [formValues, setFormValues] = useState(
    data || {
      id: null,
      point_name: "",
      start_latitude: "",
      start_longitude: "",
      end_latitude: "",
      end_longitude: "",
      is_active: true,
      google_maps_link: ""
    }
  );

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormValues((prev) => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formValues.point_name.trim()) 
      newErrors.point_name = "El nombre del punto es obligatorio.";
    
    if (!formValues.start_latitude || isNaN(parseFloat(formValues.start_latitude)))
      newErrors.start_latitude = "La latitud inicial debe ser un número válido.";
    
    if (!formValues.start_longitude || isNaN(parseFloat(formValues.start_longitude)))
      newErrors.start_longitude = "La longitud inicial debe ser un número válido.";
    
    if (!formValues.end_latitude || isNaN(parseFloat(formValues.end_latitude)))
      newErrors.end_latitude = "La latitud final debe ser un número válido.";
    
    if (!formValues.end_longitude || isNaN(parseFloat(formValues.end_longitude)))
      newErrors.end_longitude = "La longitud final debe ser un número válido.";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onSave(formValues);
    }
  };

  return (
    <div className={`GM__${theme}-modal GM__${theme}-critical-point-modal`}>
      <div className={`GM__${theme}-modal-header-container`}>
        <h3 className={`GM__${theme}-modal-header`}>
          {data ? "Editar Punto Crítico" : "Nuevo Punto Crítico"}
        </h3>
        <div className={`GM__${theme}-modal-subtitle`}>
          {data ? "Modificar información del punto crítico" : "Crear nuevo punto crítico"}
        </div>
      </div>

      <div className={`GM__${theme}-modal-content`}>
        <div className={`GM__${theme}-form-row`}>
          <div className={`GM__${theme}-form-group full-width`}>
            <label className={`GM__${theme}-form-label`} htmlFor="point_name">
              Nombre del punto
            </label>
            <input
              id="point_name"
              name="point_name"
              value={formValues.point_name}
              onChange={handleChange}
              placeholder="Ej: Curva peligrosa km 32"
              className={`GM__${theme}-form-input ${
                errors.point_name ? "is-invalid" : ""
              }`}
            />
            {errors.point_name && (
              <span className={`GM__${theme}-form-error`}>{errors.point_name}</span>
            )}
          </div>
        </div>

        <div className={`GM__${theme}-form-row`}>
          <div className={`GM__${theme}-form-group`}>
            <label className={`GM__${theme}-form-label`} htmlFor="start_latitude">
              Latitud inicial
            </label>
            <input
              id="start_latitude"
              name="start_latitude"
              type="number"
              step="0.00000001"
              value={formValues.start_latitude}
              onChange={handleChange}
              placeholder="Ej: 4.624335"
              className={`GM__${theme}-form-input ${
                errors.start_latitude ? "is-invalid" : ""
              }`}
            />
            {errors.start_latitude && (
              <span className={`GM__${theme}-form-error`}>
                {errors.start_latitude}
              </span>
            )}
          </div>

          <div className={`GM__${theme}-form-group`}>
            <label className={`GM__${theme}-form-label`} htmlFor="start_longitude">
              Longitud inicial
            </label>
            <input
              id="start_longitude"
              name="start_longitude"
              type="number"
              step="0.00000001"
              value={formValues.start_longitude}
              onChange={handleChange}
              placeholder="Ej: -74.067266"
              className={`GM__${theme}-form-input ${
                errors.start_longitude ? "is-invalid" : ""
              }`}
            />
            {errors.start_longitude && (
              <span className={`GM__${theme}-form-error`}>
                {errors.start_longitude}
              </span>
            )}
          </div>
        </div>

        <div className={`GM__${theme}-form-row`}>
          <div className={`GM__${theme}-form-group`}>
            <label className={`GM__${theme}-form-label`} htmlFor="end_latitude">
              Latitud final
            </label>
            <input
              id="end_latitude"
              name="end_latitude"
              type="number"
              step="0.00000001"
              value={formValues.end_latitude}
              onChange={handleChange}
              placeholder="Ej: 4.625782"
              className={`GM__${theme}-form-input ${
                errors.end_latitude ? "is-invalid" : ""
              }`}
            />
            {errors.end_latitude && (
              <span className={`GM__${theme}-form-error`}>
                {errors.end_latitude}
              </span>
            )}
          </div>

          <div className={`GM__${theme}-form-group`}>
            <label className={`GM__${theme}-form-label`} htmlFor="end_longitude">
              Longitud final
            </label>
            <input
              id="end_longitude"
              name="end_longitude"
              type="number"
              step="0.00000001"
              value={formValues.end_longitude}
              onChange={handleChange}
              placeholder="Ej: -74.068511"
              className={`GM__${theme}-form-input ${
                errors.end_longitude ? "is-invalid" : ""
              }`}
            />
            {errors.end_longitude && (
              <span className={`GM__${theme}-form-error`}>
                {errors.end_longitude}
              </span>
            )}
          </div>
        </div>

        <div className={`GM__${theme}-form-row`}>
          <div className={`GM__${theme}-form-group full-width`}>
            <label className={`GM__${theme}-form-label`} htmlFor="google_maps_link">
              Enlace de Google Maps (opcional)
            </label>
            <input
              id="google_maps_link"
              name="google_maps_link"
              value={formValues.google_maps_link}
              onChange={handleChange}
              placeholder="https://maps.google.com/?q=..."
              className={`GM__${theme}-form-input`}
            />
          </div>
        </div>

        <div className={`GM__${theme}-form-row`}>
          <div className={`GM__${theme}-form-group`}>
            <label className={`GM__${theme}-form-label`}>
              <input
                type="checkbox"
                name="is_active"
                checked={formValues.is_active}
                onChange={handleChange}
              />
              {" "}Punto activo
            </label>
          </div>
        </div>
      </div>

      <div className={`GM__${theme}-modal-footer`}>
        <button
          className={`GM__${theme}-btn GM__${theme}-btn-cancel`}
          onClick={onCancel}
        >
          Cancelar
        </button>
        <button
          className={`GM__${theme}-btn GM__${theme}-btn-save`}
          onClick={handleSubmit}
        >
          {data ? "Guardar Cambios" : "Crear"}
        </button>
      </div>
    </div>
  );
}

CriticalPointForm.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    point_name: PropTypes.string,
    start_latitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    start_longitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    end_latitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    end_longitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    is_active: PropTypes.bool,
    google_maps_link: PropTypes.string
  }),
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  theme: PropTypes.string.isRequired,
};